// @ts-nocheck
import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';

const COLOR_HEX = {
    green: '#1a5f2a',
    blue: '#1a4a7a',
    purple: '#5b2c6f',
    teal: '#0e6655',
    orange: '#b85c00',
};

export default function Navbar({ onToggleSidebar }) {
    const { user, logout } = useAuth();
    const { lang, setLang, t } = useI18n();
    const { mode, toggleMode, color, setColor, colors } = useTheme();
    const navigate = useNavigate();
    const [notifs, setNotifs] = useState([]);
    const [unread, setUnread] = useState(0);
    const [showNotif, setShowNotif] = useState(false);
    const [showUser, setShowUser] = useState(false);
    const notifRef = useRef(null);
    const userRef = useRef(null);

    const loadNotifs = () => {
        api
            .get('/chat/notifications')
            .then((r) => {
                const list = r.data.data || [];
                setNotifs(list);
                setUnread(list.filter((n) => !n.is_read).length);
            })
            .catch(() => {});
    };

    useEffect(() => {
        loadNotifs();
        const id = setInterval(loadNotifs, 60000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        const onDoc = (e) => {
            if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotif(false);
            if (userRef.current && !userRef.current.contains(e.target)) setShowUser(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    const markAll = async () => {
        try {
            await api.post('/chat/notifications/read-all');
            loadNotifs();
        } catch (_) {}
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const initials = (user?.name || 'U')
        .split(/\s+/)
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

    return (
        <nav className="navbar navbar-app sticky-top px-3 py-2">
            <div className="d-flex align-items-center gap-2">
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm d-lg-none"
                    onClick={onToggleSidebar}
                    aria-label="Toggle menu"
                >
                    <i className="bi bi-list fs-5" />
                </button>
                <span className="fw-semibold d-none d-md-inline text-truncate" style={{ maxWidth: 220 }}>
                    {t('appName') || 'SS Real Estate Manager'}
                </span>
            </div>

            <div className="d-flex align-items-center gap-2 ms-auto">

                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={toggleMode} title="Theme">
                    <i className={`bi ${mode === 'light' ? 'bi-moon-stars' : 'bi-sun'}`} />
                </button>

                {/* Notifications */}
                <div className="position-relative" ref={notifRef}>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm position-relative"
                        onClick={() => {
                            setShowNotif((s) => !s);
                            setShowUser(false);
                        }}
                        aria-label="Notifications"
                    >
                        <i className="bi bi-bell" />
                        {unread > 0 && (
                            <span
                                className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                                style={{ fontSize: 9 }}
                            >
                                {unread > 9 ? '9+' : unread}
                            </span>
                        )}
                    </button>
                    {showNotif && (
                        <div className="dropdown-panel card table-card shadow">
                            <div className="card-header d-flex justify-content-between align-items-center py-2">
                                <span className="fw-semibold small">Notifications</span>
                                <button type="button" className="btn btn-link btn-sm p-0" onClick={markAll}>
                                    Mark all read
                                </button>
                            </div>
                            <ul className="list-group list-group-flush notif-list">
                                {notifs.length === 0 && (
                                    <li className="list-group-item small text-muted">No notifications</li>
                                )}
                                {notifs.slice(0, 12).map((n) => (
                                    <li key={n.id} className={`list-group-item small ${n.is_read ? '' : 'fw-semibold'}`}>
                                        <div>{n.title}</div>
                                        <div className="text-muted">{n.body}</div>
                                        {n.link && (
                                            <Link
                                                to={n.link}
                                                className="small"
                                                onClick={() => setShowNotif(false)}
                                            >
                                                Open
                                            </Link>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {/* User menu */}
                <div className="position-relative" ref={userRef}>
                    <button
                        type="button"
                        className="btn btn-user-menu d-flex align-items-center gap-2"
                        onClick={() => {
                            setShowUser((s) => !s);
                            setShowNotif(false);
                        }}
                        aria-label="User menu"
                    >
                        <span className="user-avatar">{initials}</span>
                        <span className="d-none d-md-inline small text-truncate" style={{ maxWidth: 100 }}>
                            {user?.name}
                        </span>
                        <i className="bi bi-chevron-down small d-none d-md-inline" />
                    </button>
                    {showUser && (
                        <div className="dropdown-panel card table-card shadow user-menu-panel">
                            <div className="px-3 py-2 border-bottom">
                                <div className="fw-semibold small">{user?.name}</div>
                                <div className="text-muted small text-truncate">{user?.email}</div>
                                <span className="badge bg-secondary text-uppercase mt-1" style={{ fontSize: 10 }}>
                                    {user?.role}
                                </span>
                            </div>
                            <ul className="list-unstyled mb-0 py-1">
                                <li>
                                    <Link
                                        className="dropdown-item-app"
                                        to="/profile"
                                        onClick={() => setShowUser(false)}
                                    >
                                        <i className="bi bi-person me-2" />
                                        View profile
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        className="dropdown-item-app"
                                        to="/profile"
                                        onClick={() => setShowUser(false)}
                                    >
                                        <i className="bi bi-gear me-2" />
                                        Profile settings
                                    </Link>
                                </li>
                                {user?.role === 'customer' && (
                                    <li>
                                        <Link
                                            className="dropdown-item-app"
                                            to="/portal"
                                            onClick={() => setShowUser(false)}
                                        >
                                            <i className="bi bi-house-door me-2" />
                                            My portal
                                        </Link>
                                    </li>
                                )}
                                <li>
                                    <hr className="dropdown-divider my-1" />
                                </li>
                                <li>
                                    <button type="button" className="dropdown-item-app text-danger w-100 text-start border-0 bg-transparent" onClick={handleLogout}>
                                        <i className="bi bi-box-arrow-right me-2" />
                                        {t('logout') || 'Logout'}
                                    </button>
                                </li>
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </nav>
    );
}
